import moment from 'moment';
import { fyStartMonth } from './financialYear';

/**
 * Date handling for the meter dashboards' financial year filter.
 *
 * The dashboards anchored every window to `moment()` while sending the selected
 * financial year as a separate filter. The backend ANDs the two, so choosing a
 * year that does not contain today matched nothing and every card read zero.
 *
 * The fix moves the anchor rather than widening the window: a past year is read
 * from its own end instead of from today, and every window keeps its original
 * shape.
 */

/**
 * Parses the leading year out of a "YYYY-YYYY" financial year value.
 * @param {string} financialYear - Value such as '2025-2026'.
 * @returns {number|null} The starting calendar year, or null if unparseable.
 */
const startYearOf = (financialYear) => {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(String(financialYear || '').trim());
  return match ? Number(match[1]) : null;
};

/**
 * Reports whether the selected financial year is the one containing today.
 * @param {string} financialYear - Value such as '2025-2026'.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {boolean} True when today falls inside the year's nominal span.
 */
export const isCurrentFinancialYear = (financialYear, now = () => moment()) => {
  const startYear = startYearOf(financialYear);
  if (startYear === null) return false;

  const start = moment().year(startYear).month(fyStartMonth()).startOf('month');
  return now().isSameOrAfter(start) && now().isBefore(start.clone().add(1, 'year'));
};

/**
 * The point in time a dashboard should read the selected year from.
 *
 * Today for the year still running, and that year's own last day for one that
 * has closed. Every window on this page is a lookback from here, so an anchor
 * outside the year it names puts the whole window there too: "last 7 days" of
 * a closed year would ask for seven days of the year after it, while filtering
 * on the year before, and match nothing.
 *
 * This used to run ninety days past the year's end, on the premise that
 * readings keep their old year's tag for a while. The ingestion does not work
 * that way — it tags a reading by which year contains it, with no overlap — so
 * the allowance bought nothing and cost the anchor its year. It went unnoticed
 * because it happened to land on 30 June, which is where the one company
 * reporting July to June actually ends its year.
 *
 * @param {string} financialYear - Value such as '2025-2026'.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object|null} A moment, or null when the value is unparseable.
 */
export const financialYearAnchor = (financialYear, now = () => moment()) => {
  const startYear = startYearOf(financialYear);
  if (startYear === null) return null;

  const closes = moment()
    .year(startYear)
    .month(fyStartMonth())
    .startOf('month')
    .add(1, 'year')
    .subtract(1, 'day')
    .endOf('day');

  return moment.min(closes, now().clone());
};

/**
 * Resolves the two-day window the summary cards query.
 * @param {string} financialYear - Value such as '2025-2026'.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{startDateTime: string, endDateTime: string}|null} ISO bounds.
 */
export const financialYearWindow = (financialYear, now = () => moment()) => {
  const anchor = financialYearAnchor(financialYear, now);
  if (!anchor) return null;

  return {
    startDateTime: anchor.clone().subtract(2, 'days').startOf('day').toISOString(),
    endDateTime: anchor.toISOString(),
  };
};

/** Spans behind the anchor offered by the graph cards' Time Range control. */
const RELATIVE_SPANS = {
  hour: [1, 'hour'],
  day: [1, 'day'],
};

/**
 * Ranges the reader counts in whole days, and how many they expect to see.
 *
 * These are read as a count of daily bars, not as a duration. Subtracting
 * seven days from a mid-afternoon anchor starts the window inside the eighth
 * day back, and the response buckets by calendar day — so "Last 7 Days" drew
 * eight bars. Counting back from the start of the day gives exactly the number
 * on the label. "Last 30 Days" was worse: a calendar month behind the anchor
 * is thirty-one or thirty-two days depending on the month.
 */
const DAY_SPANS = {
  week: 7,
  month: 30,
};

/**
 * Ranges that run from a boundary rather than a span behind the anchor.
 *
 * These read from the anchor too, so on a past year 'this month' means the
 * last month that year could report in, not the month containing today —
 * which would fall outside the year and match nothing.
 */
const ANCHORED_STARTS = {
  thisMonth: (anchor) => anchor.clone().startOf('month'),
  thisQuarter: (anchor) => anchor.clone().startOf('quarter'),
  thisFY: (anchor, financialYear) =>
    financialYearStartOf(financialYear) || anchor.clone().startOf('year'),
};

/**
 * The first day of a financial year, from its own value.
 * @param {string} financialYear - Value such as '2025-2026'.
 * @returns {object|null} A moment at the month that year opens in, or null if
 *   unparseable.
 */
const financialYearStartOf = (financialYear) => {
  const startYear = startYearOf(financialYear);
  if (startYear === null) return null;

  return moment().year(startYear).month(fyStartMonth()).startOf('month');
};

/**
 * The Time Range control's options, so every card offers the same list and
 * every value here resolves in `relativeRangeWithin`.
 */
export const TIME_RANGES = [
  { value: 'hour', label: 'Last Hour' },
  { value: 'day', label: 'Last 24 Hours' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'thisQuarter', label: 'This Quarter' },
  { value: 'thisFY', label: 'This Financial Year' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * Resolves a graph card's range, read from the selected year's end.
 * @param {string} timeRange - hour, day, week, month or custom.
 * @param {string} financialYear - Value such as '2025-2026'.
 * @param {{start: object, end: object}} custom - Bounds for the custom range.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{start: object, end: object}} Moment bounds for the query.
 */
export const relativeRangeWithin = (
  timeRange,
  financialYear,
  custom = {},
  now = () => moment()
) => {
  const anchor = financialYearAnchor(financialYear, now) || now().clone();

  if (timeRange === 'custom') {
    return {
      start: custom.start || anchor.clone().subtract(7, 'days'),
      end: custom.end || anchor,
    };
  }

  const anchoredStart = ANCHORED_STARTS[timeRange];
  if (anchoredStart) {
    return { start: anchoredStart(anchor, financialYear), end: anchor };
  }

  const days = DAY_SPANS[timeRange];
  if (days) {
    return {
      start: anchor.clone().subtract(days - 1, 'days').startOf('day'),
      end: anchor,
    };
  }

  const [amount, unit] = RELATIVE_SPANS[timeRange] || [7, 'days'];
  return { start: anchor.clone().subtract(amount, unit), end: anchor };
};

/**
 * Names the period a summary card is showing.
 * @param {object|null} period - The row the card is built from.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {string} 'Today', 'Yesterday' or a formatted date.
 */
export const periodLabel = (period, now = () => moment()) => {
  if (!period?.period_ts) return 'No data';

  const at = moment(period.period_ts);
  if (at.isSame(now(), 'day')) return 'Today';
  if (at.isSame(now().clone().subtract(1, 'day'), 'day')) return 'Yesterday';
  return at.format('DD MMM YYYY');
};

/**
 * Builds the comparison between the two most recent reported periods.
 *
 * The query gap-fills and coalesces a missing reading to 0, returning a row for
 * every bucket in the window whether the meter reported or not, so a trailing
 * run of empty periods is padding rather than measurement and is skipped.
 *
 * @param {Array<object>} rows - Widget rows carrying `period_ts` and `sum_value`.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object|null} Values, change and labels, or null when nothing reported.
 */
export const comparePeriods = (rows = [], now = () => moment()) => {
  const ordered = (Array.isArray(rows) ? rows : [])
    .filter((row) => row && row.period_ts)
    .sort((a, b) => new Date(b.period_ts) - new Date(a.period_ts));

  const firstReported = ordered.findIndex((row) => Number(row.sum_value || 0) !== 0);
  if (firstReported === -1) return null;

  const [latest, previous] = ordered.slice(firstReported);
  const latestValue = Number(latest.sum_value || 0);
  const previousValue = Number(previous?.sum_value || 0);
  const changeValue = latestValue - previousValue;

  return {
    latestValue,
    previousValue,
    changeValue,
    change: previousValue ? (changeValue / previousValue) * 100 : 0,
    latestLabel: periodLabel(latest, now),
    previousLabel: periodLabel(previous, now),
  };
};
